import { CustomDropdown } from "@/components";
import { DEFAULT_LOADING_TEXT } from "@/constants";
import { FC, useCallback, useState, useEffect, useMemo } from "react";
import { debounce } from "lodash";
import { SORT_COLUMNS, FILTER_APPLY_TIMEOUT_MS } from "../constants";
import styles from "./styles.scss";
import { Person } from "@/services";

export const Filter: FC<{
  name: string;
  data: Person[];
  appliedFilters: Record<string, string>;
  placeholder: string;
  selectedOptions: Person[];
  onFilterChange: (item: Person) => void;
  onApplyFilters: (
    status: boolean,
    id: SORT_COLUMNS,
    selectedOptions: Person[],
  ) => void;
}> = ({ name, data, placeholder, onApplyFilters }) => {
  const isLoading = false;
  const [selectedOptions, setSelectedOptions] = useState<Person[]>([]);

  const debouncedApply = useMemo(
    () =>
      debounce((updated: Person[]) => {
        onApplyFilters(!!updated.length, name as SORT_COLUMNS, updated);
      }, FILTER_APPLY_TIMEOUT_MS),
    [name, onApplyFilters],
  );

  useEffect(() => () => debouncedApply.cancel(), [debouncedApply]);

  const handleFilterChange = useCallback(
    (item: Person) => {
      setSelectedOptions(prev => {
        const isAlreadySelected = prev.some(option => option.Id === item.Id);

        const updated = isAlreadySelected
          ? prev.filter(option => option.Id !== item.Id)
          : [...prev, item];

        debouncedApply(updated);

        return updated;
      });
    },
    [debouncedApply],
  );

  const handleClearAll = useCallback(() => {
    debouncedApply.cancel();

    setSelectedOptions([]);

    onApplyFilters(false, name as SORT_COLUMNS, []);
  }, [debouncedApply, name, onApplyFilters]);

  return (
    <CustomDropdown
      key={name}
      id={name as SORT_COLUMNS}
      containerClassName={styles.filterDropdown}
      placeholder={isLoading ? DEFAULT_LOADING_TEXT : placeholder}
      selectedOption={selectedOptions}
      options={data}
      onChange={handleFilterChange}
      onClearAll={handleClearAll}
      disabled={!data.length}
      mandatory
    />
  );
};
